/**
 * Label Utilities
 * Functions for retrieving industry labels with role-based overrides
 */

import type { IndustryLabel } from '@/contexts/SettingsContext';

/**
 * Get the appropriate label value based on user role and company industry
 *
 * This function implements the label retrieval logic with role-based overrides:
 * 1. Superadmins get labels marked with superadmin_override (highest priority)
 * 2. Admins get labels marked with admin_override
 * 3. All users get labels matching their company's industry type
 * 4. Falls back to default industry labels if no match
 *
 * @param labelType - The type of label (group, site, unit, team)
 * @param userRole - The user's role (superadmin, admin, user)
 * @param companyIndustry - The company's industry type (film, construction, etc.)
 * @param labels - Array of all configured industry labels
 * @returns The appropriate label value to use
 *
 * @example
 * // Superadmin viewing a film industry company
 * getLabelForUser('group', 'superadmin', 'film', labels)
 * // Returns label with superadmin_override if exists, otherwise 'Production' (film industry label)
 *
 * @example
 * // Admin viewing a construction industry company
 * getLabelForUser('group', 'admin', 'construction', labels)
 * // Returns label with admin_override if exists, otherwise construction industry label
 *
 * @example
 * // Regular user viewing a sports industry company
 * getLabelForUser('unit', 'user', 'sports', labels)
 * // Returns sports industry label for 'unit', no override applies
 */
export function getLabelForUser(
  labelType: 'group' | 'unit' | 'site' | 'team',
  userRole: 'superadmin' | 'admin' | 'user',
  companyIndustry: string,
  labels: IndustryLabel[]
): string {
  // Role-based overrides bypass industry matching entirely
  // Superadmins/admins see override labels regardless of company industry
  if (userRole === 'superadmin') {
    const superadminOverride = labels.find(
      l => l.label_type === labelType && l.superadmin_override === true
    );
    if (superadminOverride) return superadminOverride.label_value;
  }

  if (userRole === 'admin') {
    const adminOverride = labels.find(
      l => l.label_type === labelType && l.admin_override === true
    );
    if (adminOverride) return adminOverride.label_value;
  }

  // If no override applies, use the industry-specific label
  const industryLabel = labels.find(
    l => l.label_type === labelType && l.industry === companyIndustry
  );
  if (industryLabel) return industryLabel.label_value;

  // Fallback to default industry label
  const defaultLabel = labels.find(
    l => l.label_type === labelType && l.industry === 'default'
  );
  return defaultLabel?.label_value || labelType; // Final fallback to the label type itself
}

/**
 * Get the icon name for a label type based on user role and company industry
 * 
 * Uses the same override logic as getLabelForUser to find the matching label,
 * then returns its icon property.
 * 
 * @param labelType - The type of label (group, site, unit, team)
 * @param userRole - The user's role (superadmin, admin, user)
 * @param companyIndustry - The company's industry type (film, construction, etc.)
 * @param labels - Array of all configured industry labels
 * @returns The icon name (lucide-react icon name) or null if no icon is set
 */
export function getIconForUser(
  labelType: 'group' | 'unit' | 'site' | 'team',
  userRole: 'superadmin' | 'admin' | 'user',
  companyIndustry: string,
  labels: IndustryLabel[]
): string | null {
  // Role-based overrides bypass industry matching entirely
  // Superadmins/admins see override icons regardless of company industry
  if (userRole === 'superadmin') {
    const superadminOverride = labels.find(
      l => l.label_type === labelType && l.superadmin_override === true
    );
    if (superadminOverride) return superadminOverride.icon || null;
  }

  if (userRole === 'admin') {
    const adminOverride = labels.find(
      l => l.label_type === labelType && l.admin_override === true
    );
    if (adminOverride) return adminOverride.icon || null;
  }

  // If no override applies, use the industry-specific label
  const industryLabel = labels.find(
    l => l.label_type === labelType && l.industry === companyIndustry
  );
  if (industryLabel) return industryLabel.icon || null;

  // Fallback to default industry label
  const defaultLabel = labels.find(
    l => l.label_type === labelType && l.industry === 'default'
  );
  return defaultLabel?.icon || null;
}

